import { faQuestionCircle, IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ReactNode, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { Subject } from "rxjs";
import { CommonHelper } from "src/utils/common-helper";

export interface ConfirmDialogConfig {
    content: ReactNode;
    title: string;

    confirmText?: string;
    confirmIcon?: IconDefinition;
    confirmVariant?: string;
    onConfirm: () => void;

    discardText?: string;
    discardIcon?: IconDefinition;
    discardVariant?: string;
    onDiscard?: () => void;

    canClose?: boolean;
    className?: string;
}

interface InternalConfirmDialogConfig extends ConfirmDialogConfig {
    dialogInternalId: string;
}

const ConfirmDialogContainerComponent: React.FC = () => {
    const [dialogBoxes, setDialogBoxes] = useState<InternalConfirmDialogConfig[]>([]);
    const [closingIds, setClosingIds] = useState<Set<string>>(new Set());
    const [enteringIds, setEnteringIds] = useState<Set<string>>(new Set());

    const handleClose = (dialogInternalId: string) => {
        if (closingIds.has(dialogInternalId)) return;

        setClosingIds(prev => new Set(prev).add(dialogInternalId));
        setTimeout(() => {
            setDialogBoxes(prev => {
                return prev.filter(x => x.dialogInternalId !== dialogInternalId);
            });

            setClosingIds(prev => {
                const next = new Set(prev);
                next.delete(dialogInternalId);
                return next;
            });

            setEnteringIds(prev => {
                const next = new Set(prev);
                next.delete(dialogInternalId);
                return next;
            });
        }, 300);
    };

    const handleOpen = (config: ConfirmDialogConfig) => {
        const dialogInternalId = CommonHelper.generateRandomString(20);
        setDialogBoxes(prev => [...prev, { ...config, dialogInternalId }]);

        requestAnimationFrame(() => {
            setEnteringIds(prev => new Set(prev).add(dialogInternalId));
        });
    };

    const handleButtonClick = (config: InternalConfirmDialogConfig, handler?: () => void) => {
        handleClose(config.dialogInternalId);
        if (handler) handler();
    };

    // ESC close for topmost dialog
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape" && dialogBoxes.length > 0) {
                const topDialogId = dialogBoxes[dialogBoxes.length - 1].dialogInternalId;
                handleClose(topDialogId);
            }
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [dialogBoxes]);

    useEffect(() => {
        const sub = ConfirmDialog.ConfirmDialogGenerateRequestSubject.subscribe(config => {
            handleOpen(config);
        });

        return () => sub.unsubscribe();
    }, []);

    return (
        <div className="confirm-dialogs-container">
            {dialogBoxes.map((config, index) => {
                const isClosing = closingIds.has(config.dialogInternalId);
                const isEntering = enteringIds.has(config.dialogInternalId);
                const zIndexBase = 9000 + index * 2;

                return (

                    <div className="confirm-dialog" key={`confirm-dialog-${index}`}>
                        {/* Backdrop */}
                        <div
                            dialog-backdrop-id={config.dialogInternalId}
                            className={`backdrop fade ${isEntering && !isClosing ? "show" : ""}`}
                            onClick={() => handleClose(config.dialogInternalId)}
                            style={{ zIndex: zIndexBase }}
                        />

                        <div dialog-id={config.dialogInternalId}
                            className={[
                                "dialog",
                                config.className ?? "",
                                isClosing ? "" : isEntering ? "show" : ""
                            ].join(" ")}
                            style={{ zIndex: zIndexBase + 1 }}
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="body">
                                <FontAwesomeIcon icon={faQuestionCircle} className="icon" />
                                <div className="main">
                                    <div className="title">{config.title}</div>
                                    {
                                        config.canClose &&
                                        <div className='btn-close' onClick={() => handleClose(config.dialogInternalId)}></div>
                                    }

                                    <div className="content">
                                        {config.content}
                                    </div>
                                </div>
                            </div>
                            <div className="footer">
                                <Button
                                    variant={config.discardVariant || 'danger-outline'}
                                    onClick={() => handleButtonClick(config, config.onDiscard)}>
                                    {config.discardIcon && <FontAwesomeIcon icon={config.discardIcon} />}
                                    {config.discardText ?? "Discard"}
                                </Button>
                                <Button
                                    variant={config.confirmVariant || 'danger'}
                                    onClick={() => handleButtonClick(config, config.onConfirm)}>
                                    {config.confirmIcon && <FontAwesomeIcon icon={config.confirmIcon} />}
                                    {config.confirmText ?? "Confirm"}
                                </Button>
                            </div>
                        </div>
                    </div>
                )
            })}
        </div>
    );
};

export default ConfirmDialogContainerComponent;

export class ConfirmDialog {
    static ConfirmDialogGenerateRequestSubject = new Subject<ConfirmDialogConfig>();

    static show(config: ConfirmDialogConfig) {
        this.ConfirmDialogGenerateRequestSubject.next(config);
    }
}
